import { createAsyncApi } from "@app/state/partials/async"
import { createToast } from "@/modules/toast-message"
import { ref, watch, computed } from "vue"
import paginate from "@app/helpers/pagination"
import { toQueryString } from '@app/helpers/queryString'
import { toObject } from '@app/helpers/dot'
import { equal, wrap } from '@app/helpers/array'
import useIgnorableWatch from "@app/helpers/useIgnorableWatch"

/**
 * Appends a query string to the url for the current filtered search
 * 
 * @param {Object} data 
 */
const setHistoryState = (data) => {
    history.replaceState(
        {},
        "",
        '?' + toQueryString(
            {
                member_name: data.name ?? null,
                fields: data.fields ?? {},
                'api[page]': data.page ?? 1,
                'api[perPage]': data.perPage ?? 15,
                'api[seed]': data.seed,
                action: 'search'
            }
        )
    )
}


const applySubmissionPagination = (api) => {
    const page = ref(1)
    const perPage = ref(10)
    const seed = ref(null)

    api.api.callbacks.add('onSuccess', (response) => {
        seed.value = response.meta.seed
    })

    api.api.beforeRequest.value = data => {
        data = {
            ...data,
            page: page.value,
            perPage: perPage.value,
            seed: seed.value
        }

        setHistoryState(data)

        return data
    }

    const parameterWatch = useIgnorableWatch(
        api.api.parameters,
        () => {
            page.value = 1 //If the data changes we need to reset the page
            seed.value = null
        },
        { deep: true }
    )

    //Reset seed when page changes
    watch(page, () => {
        seed.value = null
    })

    return {
        page: page,
        perPage: perPage,
        seed: seed,

        withoutTriggeringPageUpdate: parameterWatch.without
    }
}

export default function createFACTypeMembershipsData() {
    /** 
     * @TODO: Fix asyncApi for requests made simultaneously (see singularAsyncApi)
     */

    const api = createAsyncApi(
        'bt_get_fac_type_memberships',
        'GET',
        {
            responseKey: 'fac_type_memberships',
            parameters: {
                name: '',
                fields: {},
            },
            callbacks: {
                onError: (data, original, errors) => {
                    if (data.code === 404) {
                        createToast("This FAC type couldn't be found", "error")
                        errors.value.display_error = ['Error: FAC Type couldn\'t be found']
                        errors.value.fac_type = ['Error: FAC Type doesn\'t exist'] //So we can disable the search button
                    } else if (data.code === 422) {
                        createToast(data.message, "error")
                        errors.value.display_error = ['Error: please correct filters before searching again']
                    } else {
                        createToast("Couldn't load memberships, please try again", "error")
                        errors.value.display_error = ['Error: Failed to load memberships, please try again']
                    }
                }
            }
        }
    )

    const paginationData = applySubmissionPagination(api)

    const redirect = ref(null)

    return {
        ...api,
        ...paginationData,

        pagination: computed(() => {
            if (!api.details.value?.meta ?? false) {
                return []
            }

            return paginate(
                Math.min(api.details.value.meta.total, 100),
                api.details.value.meta.current_page,
                api.details.value.meta.per_page,
            );
        }),

        /**
         * Redirecting for the pages which are search only and don't render the results
         */
        redirect: redirect,
        redirectUrl: computed(() => {
            if (!redirect.value) {
                return null
            }

            const url = new URL(redirect.value)

            toQueryString(
                {
                    member_name: api.api.parameters.value.name ?? null,
                    fields: api.api.parameters.value.fields ?? {},
                    action: 'search'
                },
                url.searchParams
            )

            return url.toString()
        }),

        setHistoryState: setHistoryState,

        setParameters: (fields) => {

            const searchParams = searchParametersToObject()

            if (searchParams.api) {
                paginationData.page.value = searchParams.api.page ?? 1
                paginationData.perPage.value = searchParams.api.perPage ?? 15
                paginationData.seed.value = searchParams.api.seed
            }

            if (searchParams.member_name) {
                api.api.parameters.value.name = searchParams.member_name
            }

            api.api.parameters.value.fields = mapSearchParamsToAnswers(
                searchParams?.fields ?? {},
                fields
            )
        },

        getMemberType: getMemberType,
        getDetailsForDisplayLocation: getDetailsForDisplayLocation,

        mappedAnswer: mappedAnswer,
    }
}

/**
 * Gets a member type from a membership
 * 
 * @param {Object} membership 
 * @returns {Object}
 */
const getMemberType = (membership) => (membership.individual_member ?? membership.organisation_member)

/**
 * Finds all fields for a given location
 * 
 * @param {String} displayLocation 
 * @param {Object} member 
 * @returns {Object}
 */
const getDetailsForDisplayLocation = (displayLocation, member) => {
    return member.member_fields
        .filter(
            (memberField) => (memberField.field?.fac_search_display_locations ?? [])
                .some(
                    (facSearchDisplayLocation) => facSearchDisplayLocation?.fac_search_display_location?.value === displayLocation
                )
        )
}

/**
 * Converts the current urls query string to an object
 * 
 * @returns {Object}
 */
const searchParametersToObject = () => {
    let fieldData = {}

    new URL(window.location).searchParams
        .forEach((value, field) => {
            //we only want the parameters which are filters or api information
            if (field === 'api[page]' || field === 'api[perPage]' || field === 'api[seed]' || field === 'member_name' || String(field).startsWith('fields[')) {
                fieldData[String(field).replaceAll('[', '.').replaceAll(']', '')] = !isNaN(value)
                    ? parseInt(value)
                    : value
            }
        })

    return toObject(fieldData) ?? {}
}

/**
 * Creates the object of valid fields we should be persisting to the state
 * 
 * @param {Object} fieldData 
 * @param {Object} searchableFields
 * @returns {Object}
 */
const mapSearchParamsToAnswers = (fieldData, searchableFields = {}) => {
    return Object.keys(fieldData)
        .reduce((fields, field) => {
            const data = searchableFields[field] !== undefined
                ? handleSearchableFieldAnswer(searchableFields[field], fieldData[field])
                : fieldData[field]

            /**
             * After converting a dot notation object to a nested one arrays are stored as objects
             * So to fix this we should compare the keys of these objects with the array equivalent
             * if they're the same we should assume we're dealing with an array not an object
             */
            if (data instanceof Object) {
                const currentFieldKeys = Object.keys(data)
                const arrayFieldKeys = Array(currentFieldKeys.length).fill(null).map((_, index) => String(index))

                if (equal(arrayFieldKeys, currentFieldKeys)) {
                    return {
                        [field]: Object.values(data),
                        ...fields
                    }
                }
            }

            //Append the field and its value to the final data
            return {
                [field]: data,
                ...fields
            }
        }, {})
}

/**
 * Handles any mappings or validation required for field answers
 * 
 * @param {Object} field 
 * @param {*}      value 
 * @returns {*}
 */
const handleSearchableFieldAnswer = (field, value) => {
    switch (field.field_type.value) {
        case 'dropdown':
            return removeInvalidDropdownOptions(field, value)
        default:
            return value
    }
}

/**
 * Removes any option we have 'selected' which is no longer present in the field options
 * 
 * @param {Object}        field 
 * @param {Object|Number} value 
 * @returns {Object|Number|undefined}
 */
const removeInvalidDropdownOptions = (field, value) => {
    if (value instanceof Object) {
        value = Object.values(value)

        return value.filter(
            (innerSelectedValue) => {
                return field.options.some(
                    (option) => option.id == innerSelectedValue
                )
            }
        )
    }

    return field.options.some(
        (option) => option.id == value
    )
        ? value
        : undefined
}

/**
 * Maps an answer into the valid output format
 * 
 * @param {Object} field 
 * @param {*} answer 
 * @returns {*}
 */
const mappedAnswer = (field, answer) => {
    switch (field.field_type.value) {
        case 'dropdown':
            return dropdownAnswerIdsToLabels(field.options, answer)
        case 'text':
            return /^https?:\/\/(.*)/.test(answer)
                ? answer.replace(/(^https?:\/\/(.*))/, '<a href="$1" target="_blank">$2</a>')
                : answer
        default:
            return answer
    }
}

/**
 * Converts a dropdown answer (option ids) into a string of the option labels
 * 
 * @param {Array} options 
 * @param {*} answer 
 * @returns {String}
 */
const dropdownAnswerIdsToLabels = (options, answer) => {
    options = options.reduce(
        (output, option) => ({
            [option.id]: option.option,
            ...output
        }),
        {}
    )

    return wrap(answer).map((option) => options?.[option] ?? option)
        .join(', ')
}